import React, { useState, useEffect } from "react";
import { Button, Modal, Badge, Form } from "react-bootstrap";
import moment from "moment";
import { MdAddChart, MdOutlineSummarize } from "react-icons/md";
import { TfiViewListAlt } from "react-icons/tfi";
import { FaEdit, FaTrash, FaTrashRestore, FaTrashAlt } from "react-icons/fa";
import { BiRefresh } from "react-icons/bi";
import { toast } from "react-toastify";
import "./ProjectBuildingBlockTab.css";
import BuildingBlockDetailModal from "./BuildingBlockDetailModal";

const ProjectBuildingBlockTab = ({
  // Data
  buildingBlocks,
  blocksLoading,
  summaryLoading,
  blockViewType,
  blockSummaries,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,

  // Handlers
  setBlockViewType,
  setShowBlockModal,
  openMapModal,
  openBlockItemsModal,
  handleEditBlock,
  getUsernameFromId,
  onSoftDelete,
  onRestore,
  onHardDelete,
  showInactive,
  setShowInactive,
  refreshBuildingBlocks,
  getTasksByBuildingBlock,
  getTicketsByBuildingBlock,
  contactNamesCache = {}, // Add this prop with default empty object

  orgId,
  projectId,
  userId,
  teamMemberDetails = [],
  clientContactPersons = [],
  partnerContactPersons = [],
  resourcesByOrg = [],
  getTasksByProject,
  getTicketsByProjectId,
  mapTaskToBuildingBlock,
  mapTicketToBuildingBlock,
  removeSpecificTaskMapping,
  removeTicketMapping,
  getAllTaskStatuses,
  taskStatusMap = {},
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedBlockForDelete, setSelectedBlockForDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [blockItemsData, setBlockItemsData] = useState({});
  const [loadingItems, setLoadingItems] = useState({});

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBlockForDetail, setSelectedBlockForDetail] = useState(null);

  // Helper function to get status columns dynamically from summaries
  // Helper function to get status columns dynamically from summaries
  const getStatusColumns = () => {
    if (buildingBlocks.length === 0) return [];

    // Debug logging
    console.log("getStatusColumns - blockSummaries:", blockSummaries);
    console.log("getStatusColumns - buildingBlocks:", buildingBlocks);

    // Try to find any block that has a summary with status keys
    let firstSummary = null;
    for (const block of buildingBlocks) {
      const summary = blockSummaries[block.buildingBlockId];
      if (summary && summary.Type === blockViewType) {
        console.log(
          `Found summary for block ${block.buildingBlockId}:`,
          summary,
        );
        firstSummary = summary;
        break;
      }
    }

    if (!firstSummary) {
      console.log("No summary found for any block");
      return [];
    }

    // Get all keys except the base ones
    const statusKeys = Object.keys(firstSummary).filter(
      (key) => key !== "Type" && key !== "Total" && key !== "ProgressPercent",
    );

    console.log("Status columns extracted:", statusKeys);
    return statusKeys;
  };

  const getStatusDisplayName = (statusKey) => {
    const specialCases = {
      MovedtoPRD: "Moved to PRD",
      ReadyforPRD: "Ready for PRD",
      SITInProgress: "SIT In Progress",
      UATInProgress: "UAT In Progress",
      DevinProgress: "Dev In Progress",
      DataUpdate: "Data Update",
    };

    if (specialCases[statusKey]) {
      return specialCases[statusKey];
    }

    return statusKey.replace(/([A-Z])/g, " $1").trim();
  };

  // Function to resolve contact GUID to name
  const resolveContactGuidToName = (guid) => {
    if (!guid) return "-";

    // Check if it's a contact GUID format
    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        guid,
      );

    if (!isGuid) {
      return guid; // It might already be a name or other identifier
    }

    // Use the contactNamesCache prop
    if (contactNamesCache && contactNamesCache[guid.toLowerCase()]) {
      return contactNamesCache[guid.toLowerCase()];
    }

    return guid.substring(0, 8) + "...";
  };

  // Function to resolve assignee name for tasks
  const resolveAssigneeName = (assignedTo) => {
    if (!assignedTo) return null;

    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        assignedTo,
      );

    if (isGuid) {
      return resolveContactGuidToName(assignedTo);
    }

    return assignedTo;
  };

  const getStatusCount = (blockId, statusKey) => {
    const summary = blockSummaries[blockId];
    if (!summary) {
      console.log(`No summary for block ${blockId}, statusKey: ${statusKey}`);
      return 0;
    }
    const value = summary[statusKey];
    console.log(`getStatusCount - block ${blockId}, ${statusKey}: ${value}`);
    return value || 0;
  };

  const statusColumns = getStatusColumns();

  // Replace the existing openMapModal and openBlockItemsModal with:
  const openDetailModal = (block) => {
    setSelectedBlockForDetail(block);
    setShowDetailModal(true);
  };

  // Fetch tasks/tickets for a specific block when expanded
  const fetchBlockItems = async (block) => {
    const blockId = block.buildingBlockId;

    // If already loaded, don't fetch again
    if (blockItemsData[blockId]) return;

    setLoadingItems((prev) => ({ ...prev, [blockId]: true }));

    try {
      let items;
      if (blockViewType === "TASK") {
        items = await getTasksByBuildingBlock(blockId);
        // Filter to only show active tasks (not soft-deleted)
        items = (items || []).filter((task) => {
          // Check if task is active
          // isActive can be true/false, 1/0, or "1"/"0"
          const isActive =
            task.isActive === true ||
            task.isActive === 1 ||
            task.isActive === "1";
          return isActive;
        });
        console.log(
          `Block ${blockId}: Loaded ${items.length} active tasks from ${(items || []).length} total`,
        );
      } else {
        items = await getTicketsByBuildingBlock(blockId);
        // Filter to only show active tickets
        items = (items || []).filter((ticket) => {
          const isActive =
            ticket.isActive === true ||
            ticket.isActive === 1 ||
            ticket.isActive === "1" ||
            ticket.isActive === null ||
            ticket.isActive === undefined;
          return isActive;
        });
      }

      setBlockItemsData((prev) => ({
        ...prev,
        [blockId]: items || [],
      }));
    } catch (error) {
      console.error("Error fetching block items:", error);
      toast.error(`Failed to load ${blockViewType.toLowerCase()}s`);
    } finally {
      setLoadingItems((prev) => ({ ...prev, [blockId]: false }));
    }
  };

  // Toggle card expand and fetch data
  const toggleCardExpand = async (block) => {
    const isExpanded = expandedCards[block.buildingBlockId];

    if (!isExpanded) {
      await fetchBlockItems(block);
    }

    setExpandedCards((prev) => ({
      ...prev,
      [block.buildingBlockId]: !prev[block.buildingBlockId],
    }));
  };

  // Refresh items when blockViewType changes
  useEffect(() => {
    setBlockItemsData({});
    setExpandedCards({});
  }, [blockViewType]);

  const getProgressColor = (percent) => {
    if (percent >= 80) return "#10b981";
    if (percent >= 50) return "#f59e0b";
    if (percent >= 25) return "#f97316";
    return "#ef4444";
  };

  const getTaskStatusBadgeClass = (status) => {
    const statusMap = {
      Completed: "ptbb-task-completed",
      "In Progress": "ptbb-task-progress",
      Open: "ptbb-task-open",
      Closed: "ptbb-task-closed",
      Pending: "ptbb-task-pending",
      Approved: "ptbb-task-approved",
      Rejected: "ptbb-task-rejected",
    };
    return statusMap[status] || "ptbb-task-default";
  };

  const getBlockStatusClass = (status) => {
    switch (status) {
      case "Completed":
        return "ptbb-block-status-completed";
      case "In Progress":
        return "ptbb-block-status-progress";
      case "On Hold":
        return "ptbb-block-status-hold";
      case "Cancelled":
        return "ptbb-block-status-cancelled";
      default:
        return "ptbb-block-status-default";
    }
  };

  const handleSoftDeleteClick = (block) => {
    setSelectedBlockForDelete(block);
    setDeleteType("soft");
    setShowDeleteConfirm(true);
  };

  const handleHardDeleteClick = (block) => {
    setSelectedBlockForDelete(block);
    setDeleteType("hard");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedBlockForDelete) return;

    setDeleting(true);
    try {
      if (deleteType === "soft") {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        await onSoftDelete(
          selectedBlockForDelete.buildingBlockId,
          String(userDetails?.userId),
        );
        toast.success("Building Block deactivated successfully");
      } else {
        await onHardDelete(selectedBlockForDelete.buildingBlockId);
        toast.warning("Building Block permanently deleted");
      }
      setShowDeleteConfirm(false);
      setSelectedBlockForDelete(null);
      setDeleteType(null);
      if (refreshBuildingBlocks) refreshBuildingBlocks();
    } catch (error) {
      toast.error(
        error.message ||
          `Failed to ${deleteType === "soft" ? "deactivate" : "delete"} building block`,
      );
    } finally {
      setDeleting(false);
    }
  };

  const getTotalProgress = () => {
    if (buildingBlocks.length === 0) return 0;
    const totalProgress = buildingBlocks.reduce((sum, block) => {
      return (
        sum + (blockSummaries[block.buildingBlockId]?.ProgressPercent || 0)
      );
    }, 0);
    return Math.round(totalProgress / buildingBlocks.length);
  };

  const activeBlocksCount = buildingBlocks.filter(
    (b) => b.active !== false,
  ).length;
  const inactiveBlocksCount = buildingBlocks.filter(
    (b) => b.active === false,
  ).length;

  // Helper function to render task/ticket item
  // Helper function to render task/ticket item
  const renderItem = (item) => {
    const isTask = blockViewType === "TASK";
    const assigneeName = isTask ? resolveAssigneeName(item.assignedTo) : null;

    // Get the status name - handle both task and ticket fields
    const getStatusName = () => {
      if (isTask) {
        // For tasks: use taskStatusName or taskStatus
        return item.taskStatusName || item.taskStatus || "N/A";
      } else {
        // For tickets: use statusName (human-readable), then status, then ticketStatus
        return item.statusName || item.status || item.ticketStatus || "N/A";
      }
    };

    // Get the ticket type name for tickets
    const getTicketTypeName = () => {
      if (!isTask) {
        return item.ticketTypeName || item.ticketType || "General";
      }
      return null;
    };

    // Get priority
    const getPriority = () => {
      if (isTask) {
        return item.taskPriority;
      }
      return item.priority;
    };

    const statusName = getStatusName();
    const statusClass = getTaskStatusBadgeClass(statusName);
    const ticketTypeName = getTicketTypeName();
    const priority = getPriority();

    return (
      <div key={isTask ? item.taskId : item.ticketId} className="ptbb-item-row">
        <div className="ptbb-item-info">
          <div className="ptbb-item-id">
            #{isTask ? item.taskId : item.ticketId}
          </div>
          <div className="ptbb-item-name">
            {isTask ? item.taskName : item.ticketName}
          </div>
        </div>
        <div className="ptbb-item-details">
          {isTask ? (
            <>
              <span className={`ptbb-task-status ${statusClass}`}>
                {statusName}
              </span>
              {assigneeName && (
                <span className="ptbb-item-assignee">
                  <i className="fas fa-user"></i> {assigneeName}
                </span>
              )}
              {priority && (
                <span
                  className={`ptbb-item-priority ptbb-priority-${priority?.toLowerCase()}`}
                >
                  <i className="fas fa-flag"></i> {priority}
                </span>
              )}
            </>
          ) : (
            <>
              <span className={`ptbb-task-status ${statusClass}`}>
                {statusName}
              </span>
              {/* <span className="ptbb-item-type">
                <i className="fas fa-tag"></i> {ticketTypeName}
              </span> */}
              {priority && (
                <span
                  className={`ptbb-item-priority ptbb-priority-${priority?.toLowerCase()}`}
                >
                  <i className="fas fa-flag"></i> {priority}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ptbb-container">
      {/* Header Section */}
      <div className="ptbb-header">
        <div className="ptbb-header-left">
          <div className="ptbb-stats">
            <div className="ptbb-stat-item">
              <span className="ptbb-stat-value">{buildingBlocks.length}</span>
              <span className="ptbb-stat-label">Total Blocks</span>
            </div>
            <div className="ptbb-stat-divider"></div>
            <div className="ptbb-stat-item">
              <span className="ptbb-stat-value ptbb-active">
                {activeBlocksCount}
              </span>
              <span className="ptbb-stat-label">Active</span>
            </div>
            <div className="ptbb-stat-divider"></div>
            <div className="ptbb-stat-item">
              <span className="ptbb-stat-value ptbb-inactive">
                {inactiveBlocksCount}
              </span>
              <span className="ptbb-stat-label">Inactive</span>
            </div>
          </div>
          <div className="ptbb-progress-ring">
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="22"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="3"
              />
              <circle
                cx="25"
                cy="25"
                r="22"
                fill="none"
                stroke={getProgressColor(getTotalProgress())}
                strokeWidth="3"
                strokeDasharray={`${getTotalProgress() * 1.38} 138`}
                strokeLinecap="round"
                transform="rotate(-90 25 25)"
              />
            </svg>
            <span className="ptbb-progress-text">{getTotalProgress()}%</span>
          </div>
        </div>

        <div className="ptbb-header-right">
          <div className="ptbb-view-toggle">
            <button
              className={`ptbb-toggle-btn ${blockViewType === "TASK" ? "active" : ""}`}
              onClick={() => setBlockViewType("TASK")}
            >
              <i className="fas fa-tasks"></i> Tasks
            </button>
            <button
              className={`ptbb-toggle-btn ${blockViewType === "TICKET" ? "active" : ""}`}
              onClick={() => setBlockViewType("TICKET")}
            >
              <i className="fas fa-ticket-alt"></i> Tickets
            </button>
          </div>

          <div className="ptbb-inactive-switch">
            <Form.Check
              type="switch"
              id="show-inactive-switch"
              label="Show Inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="ptbb-switch"
            />
          </div>

          {canEdit && (
            <button
              className="ptbb-btn-primary"
              onClick={() => setShowBlockModal(true)}
            >
              <i className="fas fa-plus"></i> New Block
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      {blocksLoading || summaryLoading ? (
        <div className="ptbb-loading">
          <div className="ptbb-spinner"></div>
          <p>Loading {blockViewType} summary...</p>
        </div>
      ) : buildingBlocks.length === 0 ? (
        <div className="ptbb-empty">
          <div className="ptbb-empty-icon">
            <i className="fas fa-cubes"></i>
          </div>
          <h3>No Building Blocks</h3>
          <p>Create your first building block to organize tasks and tickets.</p>
          {canEdit && (
            <button
              className="ptbb-btn-primary"
              onClick={() => setShowBlockModal(true)}
            >
              <i className="fas fa-plus"></i> Create Building Block
            </button>
          )}
        </div>
      ) : (
        <div className="ptbb-grid">
          {buildingBlocks.map((block) => {
            const isActive = block.active !== false;
            const summary = blockSummaries[block.buildingBlockId] || {};
            const progress = summary.ProgressPercent || 0;
            const isExpanded = expandedCards[block.buildingBlockId] || false;
            const blockItems = blockItemsData[block.buildingBlockId] || [];
            const isLoadingItems = loadingItems[block.buildingBlockId];

            return (
              <div
                key={block.buildingBlockId}
                className={`ptbb-card ${!isActive ? "inactive" : ""} ${isExpanded ? "expanded" : ""}`}
              >
                {/* Card Header */}
                <div
                  className="ptbb-card-header"
                  onClick={() => toggleCardExpand(block)}
                >
                  <div className="ptbb-card-header-left">
                    <div className="ptbb-card-icon">
                      <i className="fas fa-cube"></i>
                    </div>
                    <div className="ptbb-card-info">
                      <div className="ptbb-card-title">
                        <span className="ptbb-card-name">
                          {block.buildingBlockName}
                        </span>
                        {!isActive && (
                          <Badge bg="secondary" className="ptbb-inactive-badge">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="ptbb-card-meta">
                        <span className="ptbb-card-id">
                          {block.buildingBlockId}
                        </span>
                        {block.typeName && (
                          <span className="ptbb-card-type">
                            {block.typeName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ptbb-card-header-right">
                    <div className="ptbb-actions">
                      {isActive ? (
                        <>
                          {canEdit && (
                            <button
                              className="ptbb-action-btn map"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailModal(block);
                              }}
                              title="View and map items to this block"
                            >
                              <MdAddChart /> Manage
                            </button>
                          )}
                          {/* <button
                            className="ptbb-action-btn view"
                            onClick={(e) => {
                              e.stopPropagation();
                              openBlockItemsModal(block);
                            }}
                            title="View block items"
                          >
                            <TfiViewListAlt /> View
                          </button> */}
                          {canEdit && (
                            <>
                              <button
                                className="ptbb-action-btn edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditBlock(block);
                                }}
                                title="Edit block"
                              >
                                <FaEdit /> Edit
                              </button>
                              <button
                                className="ptbb-action-btn deactivate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSoftDeleteClick(block);
                                }}
                                title="Deactivate block"
                              >
                                <FaTrash /> Deactivate
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            className="ptbb-action-btn view"
                            onClick={(e) => {
                              e.stopPropagation();
                              openBlockItemsModal(block);
                            }}
                            title="View block items"
                          >
                            <TfiViewListAlt /> View
                          </button>
                          {canEdit && (
                            <>
                              <button
                                className="ptbb-action-btn restore"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRestore(block.buildingBlockId);
                                }}
                                title="Restore block"
                              >
                                <FaTrashRestore /> Restore
                              </button>
                              <button
                                className="ptbb-action-btn hard-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHardDeleteClick(block);
                                }}
                                title="Permanently delete block"
                              >
                                <FaTrashAlt /> Delete
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <div className="ptbb-card-stats">
                      <div className="ptbb-stat-chip">
                        <span>{summary.Total || 0} items</span>
                      </div>
                      <div className="ptbb-progress-mini">
                        <div className="ptbb-progress-bar-mini">
                          <div
                            className="ptbb-progress-fill-mini"
                            style={{
                              width: `${progress}%`,
                              backgroundColor: getProgressColor(progress),
                            }}
                          ></div>
                        </div>
                        <span className="ptbb-progress-text-mini">
                          {progress}%
                        </span>
                      </div>
                    </div>
                    <div className="ptbb-card-expand">
                      <i
                        className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                      ></i>
                    </div>
                  </div>
                </div>

                {/* Card Body - Compact View with Status Chips */}
                <div className="ptbb-card-body">
                  <div className="ptbb-status-section">
                    {/* Use statusName instead of buildingBlockStatus */}
                    <span
                      className={`ptbb-block-status ${getBlockStatusClass(block.statusName || block.buildingBlockStatus)}`}
                    >
                      {block.statusName ||
                        block.buildingBlockStatus ||
                        "Not Started"}
                    </span>
                    <div className="ptbb-poc-info">
                      {block.buildingBlockProcessPoc && (
                        <span className="ptbb-poc">
                          <i className="fas fa-user-tie"></i>{" "}
                          {resolveContactGuidToName(
                            block.buildingBlockProcessPoc,
                          )}
                        </span>
                      )}
                      {block.buildingBlockTechPoc && (
                        <span className="ptbb-poc">
                          <i className="fas fa-laptop-code"></i>{" "}
                          {resolveContactGuidToName(block.buildingBlockTechPoc)}
                        </span>
                      )}
                    </div>
                  </div>

                  {block.buildingBlockDescription && (
                    <div className="ptbb-description">
                      <i className="fas fa-align-left"></i>
                      <span>{block.buildingBlockDescription}</span>
                    </div>
                  )}

                  <div className="ptbb-date-info">
                    {block.actualStartDate && (
                      <span>
                        <i className="fas fa-calendar-alt"></i> Start:{" "}
                        {moment(block.actualStartDate).format("MMM DD, YYYY")}
                      </span>
                    )}
                    {block.actualEndDate && (
                      <span>
                        <i className="fas fa-calendar-check"></i> End:{" "}
                        {moment(block.actualEndDate).format("MMM DD, YYYY")}
                      </span>
                    )}
                  </div>

                  {/* Status Breakdown - Status Count Chips */}
                  {statusColumns.length > 0 && (
                    <div className="ptbb-status-breakdown">
                      {statusColumns.map((statusKey) => (
                        <div key={statusKey} className="ptbb-status-chip">
                          <span className="ptbb-status-chip-label">
                            {getStatusDisplayName(statusKey)}
                          </span>
                          <span className="ptbb-status-chip-value">
                            {getStatusCount(block.buildingBlockId, statusKey)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Expanded View - Tasks/Tickets List */}
                {isExpanded && (
                  <div className="ptbb-expanded-view">
                    <div className="ptbb-expanded-header">
                      <h4>
                        <i
                          className={`fas ${blockViewType === "TASK" ? "fa-tasks" : "fa-ticket-alt"}`}
                        ></i>
                        {blockViewType === "TASK" ? "Tasks" : "Tickets"} in this
                        block
                      </h4>
                      <span className="ptbb-expanded-count">
                        Total: {blockItems.length}
                      </span>
                    </div>

                    {isLoadingItems ? (
                      <div className="ptbb-items-loading">
                        <div className="ptbb-spinner-small"></div>
                        <span>Loading {blockViewType.toLowerCase()}s...</span>
                      </div>
                    ) : blockItems.length === 0 ? (
                      <div className="ptbb-items-empty">
                        <i className="fas fa-inbox"></i>
                        <p>
                          No {blockViewType.toLowerCase()}s mapped to this block
                        </p>
                        {canEdit && isActive && (
                          <button
                            className="ptbb-btn-outline"
                            onClick={() => openMapModal(block)}
                          >
                            <MdAddChart /> Map {blockViewType.toLowerCase()}s
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="ptbb-items-list">
                        {blockItems.map((item) => renderItem(item))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
        className="ptbb-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {deleteType === "soft"
              ? "Deactivate Building Block"
              : "Permanently Delete Building Block"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteType === "soft" ? (
            <>
              <div className="ptbb-modal-icon warning">
                <i className="fas fa-trash-alt"></i>
              </div>
              <p>
                Are you sure you want to deactivate{" "}
                <strong>{selectedBlockForDelete?.buildingBlockName}</strong>?
              </p>
              <p className="ptbb-modal-note">
                Deactivated blocks will be hidden from view but can be restored
                later.
              </p>
            </>
          ) : (
            <>
              <div className="ptbb-modal-icon danger">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p className="ptbb-modal-warning">
                ⚠️ WARNING: This action is irreversible!
              </p>
              <p>
                Are you sure you want to permanently delete{" "}
                <strong>{selectedBlockForDelete?.buildingBlockName}</strong>?
              </p>
              <p className="ptbb-modal-note">
                This will permanently remove the building block and cannot be
                undone.
              </p>
              <p className="ptbb-modal-caution">
                Note: Blocks with existing task/ticket mappings cannot be
                permanently deleted.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant={deleteType === "soft" ? "warning" : "danger"}
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting
              ? "Processing..."
              : deleteType === "soft"
                ? "Deactivate"
                : "Permanently Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {showDetailModal && selectedBlockForDetail && (
        <BuildingBlockDetailModal
          show={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedBlockForDetail(null);
          }}
          block={selectedBlockForDetail}
          blockViewType={blockViewType}
          orgId={orgId}
          projectId={projectId}
          userId={userId}
          contactNamesCache={contactNamesCache}
          teamMemberDetails={teamMemberDetails}
          clientContactPersons={clientContactPersons}
          partnerContactPersons={partnerContactPersons}
          resourcesByOrg={resourcesByOrg}
          getTasksByBuildingBlock={getTasksByBuildingBlock}
          getTicketsByBuildingBlock={getTicketsByBuildingBlock}
          getTasksByProject={getTasksByProject}
          getTicketsByProjectId={getTicketsByProjectId}
          mapTaskToBuildingBlock={mapTaskToBuildingBlock}
          mapTicketToBuildingBlock={mapTicketToBuildingBlock}
          removeSpecificTaskMapping={removeSpecificTaskMapping}
          removeTicketMapping={removeTicketMapping}
          onRefresh={refreshBuildingBlocks}
          getAllTaskStatuses={getAllTaskStatuses}
          taskStatusMap={taskStatusMap}
        />
      )}
    </div>
  );
};;

export default ProjectBuildingBlockTab;

// import React, { useState } from "react";
// import { Button, Modal, Badge, Form } from "react-bootstrap";
// import moment from "moment";
// import { MdAddChart } from "react-icons/md";
// import { TfiViewListAlt } from "react-icons/tfi";
// import { FaEdit, FaTrash, FaTrashRestore, FaTrashAlt } from "react-icons/fa";
// import { toast } from "react-toastify";

// const ProjectBuildingBlockTab = ({
//   // Data
//   buildingBlocks,
//   blocksLoading,
//   summaryLoading,
//   blockViewType,
//   blockSummaries,

//   // Permissions
//   hasProjectAdminRole,
//   hasProjectManagerRole,

//   // Handlers
//   setBlockViewType,
//   setShowBlockModal,
//   openMapModal,
//   openBlockItemsModal,
//   handleEditBlock,
//   getUsernameFromId,
//   onSoftDelete,
//   onRestore,
//   onHardDelete,
//   showInactive,
//   setShowInactive,
// }) => {
//   const canEdit = hasProjectAdminRole() || hasProjectManagerRole();
//   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//   const [selectedBlockForDelete, setSelectedBlockForDelete] = useState(null);
//   const [deleteType, setDeleteType] = useState(null); // 'soft' or 'hard'
//   const [deleting, setDeleting] = useState(false);

//   // Helper function to get status columns dynamically from summaries
//   const getStatusColumns = () => {
//     if (buildingBlocks.length === 0) return [];

//     // Get the first block's summary to determine available statuses
//     const firstBlockId = buildingBlocks[0]?.buildingBlockId;
//     const firstSummary = blockSummaries[firstBlockId];

//     if (!firstSummary) return [];

//     // Get all status keys (excluding Type, Total, ProgressPercent)
//     const statusKeys = Object.keys(firstSummary).filter(
//       (key) => key !== "Type" && key !== "Total" && key !== "ProgressPercent",
//     );

//     return statusKeys;
//   };

//   // Helper function to get display name for status column
//   const getStatusDisplayName = (statusKey) => {
//     // Handle special cases
//     const specialCases = {
//       MovedtoPRD: "Moved to PRD",
//       ReadyforPRD: "Ready for PRD",
//       SITInProgress: "SIT In Progress",
//       UATInProgress: "UAT In Progress",
//       DevinProgress: "Dev In Progress",
//       DataUpdate: "Data Update",
//     };

//     if (specialCases[statusKey]) {
//       return specialCases[statusKey];
//     }

//     // Add spaces before capital letters for better readability
//     return statusKey.replace(/([A-Z])/g, " $1").trim();
//   };

//   // Helper function to get status count for a block
//   const getStatusCount = (blockId, statusKey) => {
//     const summary = blockSummaries[blockId];
//     if (!summary) return 0;
//     return summary[statusKey] || 0;
//   };

//   const statusColumns = getStatusColumns();

//   const handleSoftDeleteClick = (block) => {
//     setSelectedBlockForDelete(block);
//     setDeleteType("soft");
//     setShowDeleteConfirm(true);
//   };

//   const handleHardDeleteClick = (block) => {
//     setSelectedBlockForDelete(block);
//     setDeleteType("hard");
//     setShowDeleteConfirm(true);
//   };

//   const confirmDelete = async () => {
//     if (!selectedBlockForDelete) return;

//     setDeleting(true);
//     try {
//       if (deleteType === "soft") {
//         const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//         await onSoftDelete(
//           selectedBlockForDelete.buildingBlockId,
//           String(userDetails?.userId),
//         );
//         toast.success("Building Block deactivated successfully");
//       } else {
//         await onHardDelete(selectedBlockForDelete.buildingBlockId);
//         toast.warning("Building Block permanently deleted");
//       }
//       setShowDeleteConfirm(false);
//       setSelectedBlockForDelete(null);
//       setDeleteType(null);
//     } catch (error) {
//       toast.error(
//         error.message ||
//           `Failed to ${deleteType === "soft" ? "deactivate" : "delete"} building block`,
//       );
//     } finally {
//       setDeleting(false);
//     }
//   };

//   const getStatusBadgeClass = (status) => {
//     switch (status) {
//       case "Completed":
//         return "bg-success";
//       case "In Progress":
//         return "bg-primary";
//       case "On Hold":
//         return "bg-warning";
//       case "Cancelled":
//         return "bg-danger";
//       default:
//         return "bg-secondary";
//     }
//   };

//   return (
//     <div className="project-view-fade-in">
//       <div
//         className="d-flex justify-content-between align-items-center mb-3"
//         style={{ padding: "20px" }}
//       >
//         <div>
//           <h4>Building Blocks</h4>
//           <div className="mt-2">
//             <Form.Check
//               type="switch"
//               id="show-inactive-switch"
//               label="Show Inactive Blocks"
//               checked={showInactive}
//               onChange={(e) => setShowInactive(e.target.checked)}
//               className="text-muted"
//             />
//           </div>
//         </div>

//         <div className="d-flex gap-2">
//           {/* Toggle - Visible to all roles */}
//           <Button
//             size="sm"
//             variant={blockViewType === "TASK" ? "primary" : "outline-primary"}
//             onClick={() => setBlockViewType("TASK")}
//           >
//             Tasks
//           </Button>

//           <Button
//             size="sm"
//             variant={blockViewType === "TICKET" ? "primary" : "outline-primary"}
//             onClick={() => setBlockViewType("TICKET")}
//           >
//             Tickets
//           </Button>

//           {/* Add Building Block Button - Only for Admin and Manager */}
//           {canEdit && (
//             <Button onClick={() => setShowBlockModal(true)}>
//               + Add Building Block
//             </Button>
//           )}
//         </div>
//       </div>

//       {blocksLoading || summaryLoading ? (
//         <div className="text-center py-4">
//           <div className="spinner-border text-primary" role="status"></div>
//           <p className="mt-2">Loading {blockViewType} summary...</p>
//         </div>
//       ) : buildingBlocks.length === 0 ? (
//         <p className="text-muted">No building blocks found</p>
//       ) : (
//         <div className="table-responsive" style={{ margin: "0px 14px" }}>
//           <table
//             className="table table-bordered table-hover align-middle"
//             style={{ width: "100%" }}
//           >
//             <thead className="table-dark">
//               <tr>
//                 <th>#</th>
//                 <th>Block ID</th>
//                 <th>Name</th>
//                 <th>Status</th>
//                 <th>Type</th>
//                 <th>Process POC</th>
//                 <th>Tech POC</th>
//                 <th>Start Date</th>
//                 <th>End Date</th>
//                 <th>Total</th>

//                 {/* Dynamic Status Columns */}
//                 {statusColumns.map((statusKey) => (
//                   <th key={statusKey}>{getStatusDisplayName(statusKey)}</th>
//                 ))}

//                 <th>Progress</th>
//                 <th>Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {buildingBlocks.map((block, index) => {
//                 const isActive = block.active !== false;

//                 return (
//                   <tr
//                     key={block.buildingBlockId}
//                     className={!isActive ? "table-secondary" : ""}
//                     style={!isActive ? { opacity: 0.7 } : {}}
//                   >
//                     <td>{index + 1}</td>
//                     <td>{block.buildingBlockId}</td>
//                     <td>
//                       {block.buildingBlockName}
//                       {!isActive && (
//                         <Badge bg="secondary" className="ms-2">
//                           Inactive
//                         </Badge>
//                       )}
//                     </td>
//                     <td>
//                       <span
//                         className={`badge ${getStatusBadgeClass(block.buildingBlockStatus)}`}
//                       >
//                         {block.buildingBlockStatus || "N/A"}
//                       </span>
//                     </td>
//                     <td>{block.buildingBlockTypes || "-"}</td>
//                     <td>
//                       {getUsernameFromId(block.buildingBlockProcessPoc) || "-"}
//                     </td>
//                     <td>
//                       {getUsernameFromId(block.buildingBlockTechPoc) || "-"}
//                     </td>
//                     <td>
//                       {block.actualStartDate
//                         ? moment(block.actualStartDate).format("MMM DD, YYYY")
//                         : "-"}
//                     </td>
//                     <td>
//                       {block.actualEndDate
//                         ? moment(block.actualEndDate).format("MMM DD, YYYY")
//                         : "-"}
//                     </td>
//                     <td>{blockSummaries[block.buildingBlockId]?.Total || 0}</td>

//                     {/* Dynamic Status Values */}
//                     {statusColumns.map((statusKey) => (
//                       <td key={`${block.buildingBlockId}-${statusKey}`}>
//                         {getStatusCount(block.buildingBlockId, statusKey)}
//                       </td>
//                     ))}

//                     <td style={{ minWidth: "80px" }}>
//                       <div className="progress" style={{ height: "8px" }}>
//                         <div
//                           className="progress-bar bg-success"
//                           style={{
//                             width: `${blockSummaries[block.buildingBlockId]?.ProgressPercent || 0}%`,
//                           }}
//                         ></div>
//                       </div>
//                       <small className="text-muted">
//                         {blockSummaries[block.buildingBlockId]
//                           ?.ProgressPercent || 0}
//                         %
//                       </small>
//                     </td>

//                     <td
//                       style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
//                     >
//                       {/* FOR ACTIVE BLOCKS */}
//                       {isActive && (
//                         <>
//                           {/* Map Button - Only for Admin and Manager */}
//                           {canEdit && (
//                             <Button
//                               size="sm"
//                               variant="primary"
//                               onClick={() => openMapModal(block)}
//                               style={{ padding: "10px" }}
//                               title="Map items to this block"
//                             >
//                               <MdAddChart style={{ fontSize: "16px" }} />
//                             </Button>
//                           )}

//                           {/* View Button - Visible to all roles */}
//                           <Button
//                             size="sm"
//                             variant="info"
//                             onClick={() => openBlockItemsModal(block)}
//                             style={{ padding: "10px" }}
//                             title="View block items"
//                           >
//                             <TfiViewListAlt style={{ fontSize: "16px" }} />
//                           </Button>

//                           {/* Edit Button - Only for Admin and Manager */}
//                           {canEdit && (
//                             <Button
//                               size="sm"
//                               variant="warning"
//                               onClick={() => handleEditBlock(block)}
//                               style={{ padding: "10px" }}
//                               title="Edit block"
//                             >
//                               <FaEdit style={{ fontSize: "16px" }} />
//                             </Button>
//                           )}

//                           {/* Soft Delete/Deactivate Button - Only for Admin and Manager */}
//                           {canEdit && onSoftDelete && (
//                             <Button
//                               size="sm"
//                               variant="outline-danger"
//                               onClick={() => handleSoftDeleteClick(block)}
//                               style={{ padding: "10px" }}
//                               title="Deactivate block"
//                             >
//                               <FaTrash style={{ fontSize: "14px" }} />
//                             </Button>
//                           )}
//                         </>
//                       )}

//                       {/* FOR INACTIVE BLOCKS */}
//                       {!isActive && (
//                         <>
//                           {/* View Button - Visible to all roles */}
//                           <Button
//                             size="sm"
//                             variant="info"
//                             onClick={() => openBlockItemsModal(block)}
//                             style={{ padding: "10px" }}
//                             title="View block items"
//                           >
//                             <TfiViewListAlt style={{ fontSize: "16px" }} />
//                           </Button>

//                           {/* Restore Button - Only for Admin and Manager */}
//                           {canEdit && onRestore && (
//                             <Button
//                               size="sm"
//                               variant="outline-success"
//                               onClick={() => onRestore(block.buildingBlockId)}
//                               style={{ padding: "10px" }}
//                               title="Restore block"
//                             >
//                               <FaTrashRestore style={{ fontSize: "14px" }} />
//                             </Button>
//                           )}

//                           {/* Hard Delete Button - Only for Admin and Manager (for permanent deletion) */}
//                           {/* This only shows for INACTIVE blocks */}
//                           {canEdit && onHardDelete && (
//                             <Button
//                               size="sm"
//                               variant="danger"
//                               onClick={() => handleHardDeleteClick(block)}
//                               style={{ padding: "10px" }}
//                               title="Permanently delete block"
//                             >
//                               <FaTrashAlt style={{ fontSize: "14px" }} />
//                             </Button>
//                           )}
//                         </>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       <Modal
//         show={showDeleteConfirm}
//         onHide={() => setShowDeleteConfirm(false)}
//         centered
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>
//             {deleteType === "soft"
//               ? "Deactivate Building Block"
//               : "Permanently Delete Building Block"}
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           {deleteType === "soft" ? (
//             <>
//               <p>
//                 Are you sure you want to deactivate{" "}
//                 <strong>{selectedBlockForDelete?.buildingBlockName}</strong>?
//               </p>
//               <p className="text-muted small">
//                 Deactivated blocks will be hidden from view but can be restored
//                 later.
//               </p>
//             </>
//           ) : (
//             <>
//               <p className="text-danger">
//                 ⚠️ WARNING: This action is irreversible!
//               </p>
//               <p>
//                 Are you sure you want to permanently delete{" "}
//                 <strong>{selectedBlockForDelete?.buildingBlockName}</strong>?
//               </p>
//               <p className="text-muted small">
//                 This will permanently remove the building block and cannot be
//                 undone.
//               </p>
//               <p className="text-warning small">
//                 Note: Blocks with existing task/ticket mappings cannot be
//                 permanently deleted.
//               </p>
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowDeleteConfirm(false)}
//             disabled={deleting}
//           >
//             Cancel
//           </Button>
//           <Button
//             variant={deleteType === "soft" ? "warning" : "danger"}
//             onClick={confirmDelete}
//             disabled={deleting}
//           >
//             {deleting
//               ? "Processing..."
//               : deleteType === "soft"
//                 ? "Deactivate"
//                 : "Permanently Delete"}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </div>
//   );
// };

// export default ProjectBuildingBlockTab;
